import type { Scheduler, TimerHandle } from './scheduler';

/**
 * A group of waits that can be suspended as a unit.
 *
 * The mission runtime pauses for parent controls, for teaching moments and (in Phase 3) for lost
 * tracking. Every one of those has to stop the 8/15/25 s ladder rather than let it run behind the
 * overlay: a child who comes back from a pause to find the round has already escalated has been
 * penalised for their parent answering the door.
 */

export type RoundTimerId = number;

export interface TimerGroup {
  after(delayMs: number, callback: () => void): RoundTimerId;
  cancel(id: RoundTimerId): void;
  cancelAll(): void;
  pause(): void;
  resume(): void;
  readonly paused: boolean;
}

interface GroupTimer {
  id: RoundTimerId;
  callback: () => void;
  /** While running: when it is due. While paused: unused. */
  dueAt: number;
  /** While paused: how much of the wait is left. */
  remainingMs: number;
  handle: TimerHandle | null;
}

export function createTimerGroup(scheduler: Scheduler): TimerGroup {
  const timers = new Map<RoundTimerId, GroupTimer>();
  let nextId: RoundTimerId = 1;
  let paused = false;

  const arm = (timer: GroupTimer, delayMs: number): void => {
    timer.dueAt = scheduler.now() + delayMs;
    timer.handle = scheduler.setTimeout(() => {
      timers.delete(timer.id);
      timer.callback();
    }, delayMs);
  };

  return {
    after(delayMs: number, callback: () => void): RoundTimerId {
      const id = nextId;
      nextId += 1;
      const timer: GroupTimer = {
        id,
        callback,
        dueAt: scheduler.now() + delayMs,
        remainingMs: delayMs,
        handle: null,
      };
      timers.set(id, timer);
      // A timer created during a pause waits with everything else rather than starting behind it.
      if (!paused) arm(timer, delayMs);
      return id;
    },

    cancel(id: RoundTimerId): void {
      const timer = timers.get(id);
      if (timer === undefined) return;
      if (timer.handle !== null) scheduler.clearTimeout(timer.handle);
      timers.delete(id);
    },

    cancelAll(): void {
      for (const timer of timers.values()) {
        if (timer.handle !== null) scheduler.clearTimeout(timer.handle);
      }
      timers.clear();
    },

    pause(): void {
      // A second pause must not re-derive "time remaining" from a clock that has since moved on.
      if (paused) return;
      paused = true;
      const now = scheduler.now();
      for (const timer of timers.values()) {
        if (timer.handle !== null) scheduler.clearTimeout(timer.handle);
        timer.handle = null;
        timer.remainingMs = Math.max(0, timer.dueAt - now);
      }
    },

    resume(): void {
      if (!paused) return;
      paused = false;
      for (const timer of timers.values()) arm(timer, timer.remainingMs);
    },

    get paused(): boolean {
      return paused;
    },
  };
}
